import type {
  FormState,
  FormStateValue,
  ResolvedDataSource,
} from '@de/providers/DataSourceManager.types';
import type { Lead, Option } from '@de/types/types';
import { formStateValuesEqual } from '@de/utils/form-state';
import { buildOptionFromFormState } from './buildOptionFromFormState';
import type { BoundComponentInfo } from './findBoundComponent';
import { isLeadSubmitStateKey } from './leadSubmitState';
import { resolveDisplayLabel } from './resolveDisplayLabel';
import { resolveSourceSlug } from './resolveSourceSlug';
import { isDcsKey, type DcsKey } from '@de/utils/taxonomy/fetchTaxonomy';

/** Shape the bridge passes into {@link translateFormStateChange}. */
export interface TranslateFormStateChangeArgs {
  /** Active step id; the resulting Option(s) are written under this key. */
  readonly stepId: string;
  /** Previous DSM `formState` snapshot (held in a ref by the caller). */
  readonly prev: FormState;
  /** Latest DSM `formState` after the just-committed update. */
  readonly next: FormState;
  /** Resolves a `formKey` to its bound component metadata. */
  readonly resolveBound: (formKey: string) => BoundComponentInfo | undefined;
  /** Resolves a DSM source name to its current snapshot. */
  readonly resolveSource: (name: string) => ResolvedDataSource | undefined;
  /** Resolves display labels for unbound DCS values seeded by the host. */
  readonly translateDcsValue?: (key: DcsKey, slug: string) => string;
  /**
   * Host `initialFormState` keys. An unbound one stays in the Data Source
   * Manager: it must not fall back to `leadFieldAssociated = formKey`,
   * which would copy it into lead inputs. Bound keys and the DCS /
   * country-code seeds are unchanged.
   */
  readonly unboundHostKeys?: ReadonlySet<string>;
  /** `BoundFieldIndex.formByKey`: fields inside a `platform.form`, kept out of `flowState`. */
  readonly formByKey?: Readonly<Record<string, string>>;
  /** DE `updateFlowState` -- called once per step at most per invocation. */
  readonly commit: (
    stepId: string,
    data: {
      option: Option | Option[];
      leadFieldAssociated?: keyof Lead;
    },
  ) => void;
}

/**
 * Diff `prev` vs `next` and translate the **full** `next` form state
 * into a single DE `flowState` write under `stepId`.
 *
 * Why translate the full snapshot instead of just the diff
 * --------------------------------------------------------
 * `dynamicExperienceReducer` replaces `flowState[stepId]` wholesale on
 * every `UPDATE_FLOW_STATE` action. If we committed only the changed
 * keys, the second selection in a multi-`formKey` step would clobber
 * the first (PR #2372 review §2). Translating the complete `next`
 * snapshot means the committed `Option[]` always reflects every
 * surviving selection, and a no-op diff still short-circuits before
 * any work runs.
 *
 * Behavior (per plan §3.4 + Q2, updated for review §2):
 *   - No changes (`prev` ≡ `next`) or `stepId` blank → no-op.
 *   - `leadSubmit__*` keys and `formByKey` fields never reach `flowState`.
 *   - Otherwise, translate every key in `next` (in iteration order)
 *     into Option(s) and commit them as the new step state.
 *   - A native list value expands to one Option per selected entry so
 *     multi-select form state reaches DE as discrete selections. A
 *     string value (including legacy CSV) stays a single Option — CSV
 *     expand remains gated by `_meta.isArray` on the Option itself.
 *   - Single resulting Option → `{ option: <Option>, leadFieldAssociated }`.
 *     A bound component supplies its association via `_meta`; an unbound key
 *     (the seeded DCS triad / `countryCode`) falls back to the formKey so the
 *     association survives the reducer's wholesale rebuild (see
 *     {@link buildOptionForKey}).
 *   - Multiple → `{ option: <Option[]> }` with NO step-level
 *     `leadFieldAssociated`. Each Option already carries its own
 *     `leadFieldAssociated`; stamping the step level with the first
 *     option's field is ambiguous for a multi-`formKey` step and makes
 *     `buildFlowContext` map that single field to the whole value array
 *     (e.g. `context.degree` resolving to `[degree, category, subject]`),
 *     which breaks per-field `{{context.<field>}}` interpolation. Lead
 *     capture reads the per-option fields for array payloads, so the
 *     step-level field is not needed here.
 *
 * Errors thrown by `resolveBound` / `resolveSource` are caught per
 * formKey so one bad lookup never blocks the rest of the commit.
 */
export function translateFormStateChange(
  args: TranslateFormStateChangeArgs,
): void {
  if (!args.stepId) return;

  // Diff is only used as a guard: if nothing changed, skip the commit
  // entirely. The committed payload below is built from the *full*
  // `next` snapshot so prior selections survive the reducer's
  // wholesale `flowState[stepId]` replacement.
  const isBridged = (key: string): boolean =>
    !isLeadSubmitStateKey(key) &&
    !(args.formByKey && hasOwn(args.formByKey, key));
  if (!hasAnyChange(args.prev, args.next, isBridged)) return;

  const options: Option[] = [];
  let leadFieldAssociated: string | undefined;

  for (const formKey of Object.keys(args.next)) {
    if (!isBridged(formKey)) continue;
    if (isUnboundHostKey(formKey, args)) continue;
    const values = selectionValues(args.next[formKey]);
    for (const value of values) {
      let option: Option | undefined;
      try {
        option = buildOptionForKey(formKey, value, args);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(
          `[ConfigurableStep] translateFormStateChange: resolver threw for "${formKey}"`,
          err,
        );
        continue;
      }
      if (!option) continue;
      options.push(option);
      if (!leadFieldAssociated && option.leadFieldAssociated) {
        leadFieldAssociated = option.leadFieldAssociated;
      }
    }
  }

  if (options.length === 0) return;

  const payload = options.length === 1 ? options[0] : (options as Option[]);

  // Only a single-`formKey` commit carries a step-level
  // `leadFieldAssociated`. For a multi-`formKey` step each Option carries
  // its own, and a single step-level field would be ambiguous (see the
  // header doc) — so omit it and let `buildFlowContext` emit per-option
  // scalar aliases.
  const stepLeadField = options.length === 1 ? leadFieldAssociated : undefined;

  args.commit(args.stepId, {
    option: payload,
    ...(stepLeadField && {
      leadFieldAssociated: stepLeadField as keyof Lead,
    }),
  });
}

function isUnboundHostKey(
  formKey: string,
  args: TranslateFormStateChangeArgs,
): boolean {
  if (!args.unboundHostKeys?.has(formKey)) return false;
  try {
    return args.resolveBound(formKey) === undefined;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(
      `[ConfigurableStep] translateFormStateChange: resolver threw for "${formKey}"`,
      err,
    );
    return true;
  }
}

function hasOwn(record: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function hasAnyChange(
  prev: FormState,
  next: FormState,
  isBridged: (key: string) => boolean,
): boolean {
  for (const key of Object.keys(next)) {
    if (!isBridged(key)) continue;
    if (!formStateValuesEqual(prev[key], next[key])) return true;
  }
  for (const key of Object.keys(prev)) {
    if (!isBridged(key)) continue;
    if (!(key in next) && prev[key] !== undefined) return true;
  }
  return false;
}

/**
 * Values to emit as Options for one form key.
 *
 * Native lists expand one Option per entry (empty list → one cleared
 * Option, matching a cleared string). Strings stay one Option so legacy
 * CSV + `_meta.isArray` continues to round-trip as a single payload.
 */
function selectionValues(value: FormStateValue | undefined): string[] {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) {
    return value.length === 0 ? [''] : Array.from(value);
  }
  return [''];
}

function buildOptionForKey(
  formKey: string,
  value: string,
  args: TranslateFormStateChangeArgs,
): Option | undefined {
  const bound = args.resolveBound(formKey);
  const source = bound?.source ? args.resolveSource(bound.source) : undefined;
  const label =
    !bound?.source && isDcsKey(formKey) && args.translateDcsValue
      ? args.translateDcsValue(formKey, value)
      : resolveDisplayLabel(value, bound, source);
  // Slug-aware Option emission: when the bound source carries a
  // `slug` field on each item, also surface it on the committed
  // Option so `evaluateCondition`'s `COMBINATION_SELECTED` matcher
  // resolves against canonical slugs regardless of what the DSL
  // author bound to `_bind.optionValueField` (typically an id for
  // cascading Mobius queries — see resolveSourceSlug for the full
  // decoupling rationale). `undefined` when the source has no slug
  // (non-Mobius data sources, single-source flows, etc.) so we never
  // emit a fake slug.
  const slug = resolveSourceSlug(value, bound, source);
  // Lead-field association: a bound component supplies its own via
  // `_meta.leadFieldAssociated` (which may be intentionally undefined, so a
  // bound-but-unassociated component keeps that). Keys with no resolved
  // component at all (e.g. the DCS triad
  // `degree`/`category`/`subject` and `countryCode`, which are seeded
  // into `formState` by `initializeAutoPopulation`/`deriveInitialFormState`
  // but have no DSL Select on an offerings-listing entry) fall back to the
  // `formKey` itself. This mirrors the seed's own shape (where
  // `optionId === leadFieldAssociated === <field>`) so that the reducer's
  // wholesale `flowState[stepId]` rebuild — triggered by any later
  // `formState` change such as the async IP `countryCode` bridge — does not
  // strip the association. Without the fallback, `getFullOptionsFromFlowState`
  // can no longer find `degree`/`category`/`subject` after the first rebuild
  // and the result/eject URL falls back to `general` even though the
  // offerings source (reading `formState.*` directly) still has the values.
  const leadFieldAssociated = bound ? bound.leadFieldAssociated : formKey;
  // `customField` is a pure opt-in allowlist handle (no formKey fallback
  // like `leadFieldAssociated`'s DCS-triad seed-compat shim above) — it
  // only round-trips when the DSL author explicitly set
  // `_meta.customField` on the bound component.
  const customField = bound?.customField;
  // Same opt-in posture as `customField`: only forward when the DSL
  // Select authored `_meta.isArray: true` so CSV expand stays gated.
  const isArray = bound?.isArray === true ? true : undefined;
  return buildOptionFromFormState({
    formKey,
    value,
    label,
    ...(slug ? { slug } : {}),
    leadFieldAssociated,
    customField,
    isArray,
  });
}
